import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AppError } from '../app-error.model';

/**
 * Dismissable modal for the "modal" tier: blocking errors (red) and recoverable 4xx (warning-styled).
 * Since the configurator is an internal tool, the correlation id and raw technical detail are shown behind
 * an expandable section to help triage.
 */
@Component({
  selector: 'app-error-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="modal-header">
      <h4 class="modal-title" [class.text-danger]="!isWarning()" [class.text-warning-emphasis]="isWarning()">
        <i class="fa-solid fa-triangle-exclamation me-2"></i>{{ error.title }}
      </h4>
      <button type="button" class="btn-close" aria-label="Luk" (click)="close()"></button>
    </div>
    <div class="modal-body">
      <p class="mb-2" style="white-space: pre-line;">{{ error.message }}</p>

      @if (error.correlationId) {
        <p class="text-muted small mb-2">
          Reference: <code>{{ error.correlationId }}</code>
        </p>
      }

      @if (technical()) {
        <div class="d-flex align-items-center gap-3">
          <button type="button" class="btn btn-link btn-sm px-0" (click)="showDetails.set(!showDetails())">
            {{ showDetails() ? 'Skjul tekniske detaljer' : 'Vis tekniske detaljer' }}
          </button>
          @if (showDetails()) {
            <div class="form-check form-switch mt-1">
              <input
                type="checkbox"
                role="switch"
                id="errorModalFullWidth"
                class="form-check-input"
                [checked]="!wrapLines()"
                (change)="wrapLines.set(!wrapLines())"
              />
              <label for="errorModalFullWidth" class="form-check-label" style="text-wrap: nowrap;">Fuld bredde</label>
            </div>
          }
        </div>
        @if (showDetails()) {
          <pre
            class="bg-light border rounded p-2 small mb-0"
            [style.max-height.rem]="28"
            [style.overflow]="'auto'"
            [style.white-space]="wrapLines() ? 'pre-wrap' : 'pre'"
            [style.overflow-wrap]="wrapLines() ? 'anywhere' : 'normal'"
          >{{ technical() }}</pre>
        }
      }
    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" (click)="close()">Luk</button>
    </div>
  `,
})
export class ErrorModalComponent {
  error!: AppError;
  readonly showDetails = signal(false);
  /** true = wrap long stack lines within the modal; false = full width with horizontal scroll. */
  readonly wrapLines = signal(true);

  constructor(private readonly activeModal: NgbActiveModal) {}

  /** Recoverable errors (404/409, client-side notices) are warnings, not failures. */
  isWarning(): boolean {
    return this.error?.severity === 'recoverable';
  }

  /** Pretty-printed raw error body for the expandable technical section. */
  technical(): string | null {
    const raw = this.error?.raw as { error?: unknown } | undefined;
    const body = raw?.error ?? raw;
    if (!body) {
      return null;
    }
    try {
      return typeof body === 'string' ? body : JSON.stringify(body, null, 2);
    } catch {
      return null;
    }
  }

  close(): void {
    this.activeModal.close();
  }
}
